import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';

export interface Notification {
  id: string;
  tenantId: string;
  userId: string | null;
  type: string;
  category: string;
  title: string;
  message: string;
  data: { route?: string; [key: string]: unknown } | null;
  priority: string;
  readAt: string | null;
  createdAt: string;
}

interface NotificationListResponse {
  notifications: Notification[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
}

interface UnreadCountResponse {
  count: number;
}

export function useNotifications(page = 1, limit = 20) {
  return useQuery({
    queryKey: ['notifications', page, limit],
    queryFn: () =>
      apiClient.get<NotificationListResponse>('/notifications', { page, limit }),
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () =>
      apiClient.get<UnreadCountResponse>('/notifications/unread-count'),
    refetchInterval: 60_000,
  });
}

export function useMarkAsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.patch<{ success: boolean }>(`/notifications/${id}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useMarkAllAsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiClient.patch<{ success: boolean }>('/notifications/read-all'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

// ─── Notification Preferences ───────────────────────────────

export interface NotificationPreference {
  category: string;
  email: boolean;
  sms: boolean;
  whatsapp: boolean;
  push: boolean;
}

export interface PreferencesResponse {
  preferences: NotificationPreference[];
  channels: {
    email: boolean;
    sms: boolean;
    whatsapp: boolean;
    push: boolean;
  };
  /** Numéro de téléphone du profil (E.164) — null si non renseigné */
  phone: string | null;
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: ['notifications', 'preferences'],
    queryFn: () => apiClient.get<PreferencesResponse>('/notifications/preferences'),
  });
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (preferences: NotificationPreference[]) =>
      apiClient.put<{ updated: number }>('/notifications/preferences', { preferences }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', 'preferences'] });
    },
  });
}

/**
 * The public VAPID key, fetched only once push is known to be usable.
 *
 * The endpoint answers 404 `PUSH_NOT_CONFIGURED` on a deployment with no
 * VAPID key, so requesting it unconditionally logs a console error on every
 * page load for a feature that is simply switched off. `enabled` gates it on
 * the server's own signal — `channels.push` from the preferences endpoint —
 * and on the browser supporting push at all.
 */
export function useVapidKey(enabled = true) {
  return useQuery({
    queryKey: ['notifications', 'vapid-key'],
    queryFn: () => apiClient.get<{ vapidKey: string }>('/notifications/push/vapid-key'),
    retry: false,
    enabled,
  });
}

export function usePushSubscribe() {
  return useMutation({
    mutationFn: (subscription: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
      apiClient.post<{ subscribed: boolean }>('/notifications/push/subscribe', subscription),
  });
}

export function usePushUnsubscribe() {
  return useMutation({
    mutationFn: (endpoint: string) =>
      apiClient.post<{ unsubscribed: boolean }>('/notifications/push/unsubscribe', { endpoint }),
  });
}
